// Centralized SEO metadata helper.
// Returns per-route title, description, OG/Twitter, canonical, robots, and image.
// Used by <SEO /> component on every page.

export const SITE_URL = 'https://aitoolhub.ai';
export const DEFAULT_OG_IMAGE =
  'https://storage.googleapis.com/gpt-engineer-file-uploads/kGvXAzmoH4gGf62ytLPdV0Cg3K93/social-images/social-1766960658285-AITOOLHUB.png';
export const SITE_NAME = 'AI ToolHub';
export const TWITTER_HANDLE = '@AIToolHub';
export const THEME_COLOR = '#0b0b0f';

export type OgType = 'website' | 'article' | 'product' | 'profile';

export interface SEOMeta {
  title: string;
  description: string;
  canonical: string;
  ogType: OgType;
  ogImage: string;
  robots?: string; // default: "index,follow,max-image-preview:large"
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string;
}

/** Build an absolute canonical URL from a pathname.
 *  - Strips tracking params (utm_*, gclid, fbclid, ref, msclkid).
 *  - Lowercases path.
 *  - Removes trailing slash (except for "/").
 *  - Keeps semantic params if explicitly preserved.
 */
export function buildCanonical(pathname: string, opts?: { keepParams?: Record<string, string | undefined> }): string {
  let path = (pathname || '/').split('?')[0].split('#')[0];
  path = path.toLowerCase();
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

  const search = new URLSearchParams();
  if (opts?.keepParams) {
    Object.entries(opts.keepParams).forEach(([k, v]) => {
      if (v) search.set(k, v);
    });
  }
  const qs = search.toString();
  return `${SITE_URL}${path}${qs ? `?${qs}` : ''}`;
}

const DEFAULT_DESC =
  'Search 600+ AI tools by task, check pricing, find alternatives to the tools you already use and compare up to 4 side by side. Free to browse.';

const truncate = (s: string, n = 160) => {
  if (!s) return '';
  const clean = s.replace(/\s+/g, ' ').trim();
  return clean.length <= n ? clean : clean.slice(0, n - 1).replace(/[,;:\.\-\s]+\S*$/, '') + '…';
};

export interface RouteContext {
  pathname: string;
  data?: Record<string, any>;
}

/** Returns SEO metadata for any route. Call this from any page. */
export function getMeta(ctx: RouteContext): SEOMeta {
  const { pathname, data = {} } = ctx;
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const canonical = buildCanonical(pathname);

  // -- Static routes --
  const staticMap: Record<string, Partial<SEOMeta> & { title: string; description: string }> = {
    '/': {
      title: 'AI ToolHub — Find AI Tools, Alternatives & Side-by-Side Comparisons',
      description: DEFAULT_DESC,
    },
    '/tools': {
      title: 'All AI Tools — Browse 500+ Curated Options | AI ToolHub',
      description: 'Browse our full directory of 500+ AI tools. Filter by category, pricing, and use case to find the perfect match for your workflow.',
    },
    '/categories': {
      title: 'AI Tool Categories — Browse 20+ Categories | AI ToolHub',
      description: 'Browse 500+ AI tools organized into 20+ categories including writing, design, video, coding, marketing, and more.',
    },
    '/search': {
      title: 'Search AI Tools | AI ToolHub',
      description: 'Search 500+ AI tools with natural-language AI-powered search. Find the right tool by describing what you need.',
      robots: 'noindex,follow',
    },
    '/middle-east': {
      title: 'Middle East AI Startups — UAE, Dubai, Saudi & More | AI ToolHub',
      description: 'Discover AI tools built by Middle East startups. Filter by country, UAE and Dubai to find regional AI products.',
    },
    '/best-free-ai-tools': {
      title: 'Best Free AI Tools in 2026 — Free & Freemium Picks | AI ToolHub',
      description: 'The best free AI tools you can use today: fully free apps plus freemium tools with a free forever plan, each reviewed with its real free-tier limits.',
    },
    '/alternatives': {
      title: 'AI Tool Alternatives Finder | AI ToolHub',
      description: 'Find the best alternatives to popular AI tools. Side-by-side comparisons, pricing, and feature breakdowns.',
    },
    '/compare': {
      title: 'Compare AI Tools Side-by-Side | AI ToolHub',
      description: 'Pick up to 4 AI tools and compare features, pricing, trust scores and reviews side-by-side.',
    },
    '/compare/vibe-coding': {
      title: 'Vibe Coding Tools Comparison — Lovable vs Bolt vs v0 | AI ToolHub',
      description: 'Compare the top vibe coding AI tools head-to-head: features, pricing, pros & cons, and user reviews.',
    },
    '/compare/mobile-frameworks': {
      title: 'AI Mobile Framework Comparison | AI ToolHub',
      description: 'Compare AI-powered mobile development frameworks side-by-side: features, pricing, and use cases.',
    },
    '/news': {
      title: 'AI News — Latest Artificial Intelligence Updates | AI ToolHub',
      description: 'Breaking AI news, model releases, funding rounds and product launches from across the AI ecosystem.',
    },
    '/blog': {
      title: 'AI ToolHub Blog — Tutorials, Comparisons & Guides',
      description: 'In-depth tutorials, AI tool comparisons, and guides to help you get more from artificial intelligence.',
    },
    '/jobs': {
      title: 'AI Jobs — Find Your Next Role in AI | AI ToolHub',
      description: 'Open AI, ML and data roles from top companies. Search by role, location and seniority.',
    },
    '/post-job': {
      title: 'Post an AI Job — Hire Top AI Talent | AI ToolHub',
      description: 'List your open AI, ML, or data role to AI-focused candidates. Simple posting, fast results.',
    },
    '/events': {
      title: 'AI Events — Conferences, Webinars & Meetups | AI ToolHub',
      description: 'Discover upcoming AI conferences, virtual webinars, and local meetups. Stay current with the AI community.',
    },
    '/events/calendar': {
      title: 'AI Events Calendar | AI ToolHub',
      description: 'Browse the full calendar of upcoming AI events, conferences and webinars worldwide.',
    },
    '/events/pricing': {
      title: 'AI Event Listing Pricing | AI ToolHub',
      description: 'Promote your AI conference, webinar, or workshop. Choose from free and paid listing packages.',
    },
    '/events/submit': {
      title: 'Submit an AI Event | AI ToolHub',
      description: 'List your AI event, webinar, or conference on AI ToolHub and reach AI professionals.',
    },
    '/income-blueprints': {
      title: 'AI Income Blueprints — Make Money with AI Tools | AI ToolHub',
      description: 'Step-by-step income blueprints showing how to earn money using AI tools. Real strategies, estimated earnings, and required tools.',
    },
    '/income-reports': {
      title: 'AI Income Reports — Real Earnings from AI Creators | AI ToolHub',
      description: 'Real income reports from creators using AI tools. See what people earn, how, and which tools they use.',
    },
    '/marketplace': {
      title: 'Vibe Coders Marketplace — Hire AI Specialists | AI ToolHub',
      description: 'Hire verified AI developers, designers, and automation experts. The trusted marketplace for vibe coders and AI specialists.',
    },
    '/marketplace/become-coder': {
      title: 'Become a Vibe Coder — Earn with AI | AI ToolHub',
      description: 'Join the Vibe Coders Marketplace as a verified AI specialist. Showcase your skills and earn from AI projects.',
    },
    '/digital-products': {
      title: 'AI Digital Products — Prompt Packs, Templates & More | AI ToolHub',
      description: 'Buy and sell AI prompt packs, templates, ebooks, and courses. Creators earn 85% on every sale.',
    },
    '/sell-digital-product': {
      title: 'Sell Digital Products — Earn 85% Per Sale | AI ToolHub',
      description: 'List your AI prompt packs, templates, or courses on AI ToolHub. Keep 85% of every sale.',
    },
    '/pricing': {
      title: 'Pricing — Free & Verified Plans | AI ToolHub',
      description: 'AI ToolHub is free for users. Tool owners can upgrade to Verified Basic ($29/mo), Pro ($79/mo), or Premium ($149/mo).',
    },
    '/about': {
      title: 'About AI ToolHub — Curated AI Tools Directory',
      description: 'AI ToolHub is a curated directory of AI tools, helping individuals and teams discover, compare, and choose AI tools.',
    },
    '/contact': {
      title: 'Contact AI ToolHub — Get in Touch',
      description: 'Reach the AI ToolHub team. Email hello@aitoolhub.ai or send us a message for partnerships, support, and press inquiries.',
    },
    '/help': {
      title: 'Help Center — AI ToolHub Support',
      description: 'Find answers, guides, and support resources for using AI ToolHub, submitting tools, and managing your account.',
    },
    '/help-center': {
      title: 'AI ToolHub Help Center — Guides, Tutorials & Account Help',
      description: 'Step-by-step guides and tutorials for browsing AI tools, listing your product, selling templates, and getting account help on AI ToolHub.',
    },
    '/faq': {
      title: 'AI ToolHub FAQ — Frequently Asked Questions',
      description: 'Answers to common questions about AI ToolHub, AI tools, the marketplace, pricing, and submissions.',
    },
    '/submit': {
      title: 'Submit Your AI Tool — Free Listing | AI ToolHub',
      description: 'Submit your AI tool to AI ToolHub for free. Reach people actively searching for AI tools.',
    },
    '/privacy': {
      title: 'Privacy Policy | AI ToolHub',
      description: 'How AI ToolHub collects, uses, and protects your personal data. Read our full privacy policy.',
    },
    '/terms': {
      title: 'Terms of Service | AI ToolHub',
      description: 'The terms governing your use of AI ToolHub, including user accounts, content, and payments.',
    },
    '/legal': {
      title: 'Legal Center — Policies & Agreements | AI ToolHub',
      description: 'All legal documents for AI ToolHub: terms, privacy, refund, seller, buyer, and creator agreements.',
    },
    '/refund-policy': {
      title: 'Refund Policy | AI ToolHub',
      description: 'AI ToolHub refund policy for digital products, marketplace services, and verified plans.',
    },
    '/seller-terms': {
      title: 'Seller Terms | AI ToolHub Marketplace',
      description: 'Terms and conditions for sellers on the AI ToolHub digital products marketplace.',
    },
    '/buyer-terms': {
      title: 'Buyer Terms | AI ToolHub Marketplace',
      description: 'Terms and conditions for buyers purchasing on the AI ToolHub digital products marketplace.',
    },
    '/creator-terms': {
      title: 'Creator Terms | AI ToolHub',
      description: 'Terms and conditions for creators publishing content and tools on AI ToolHub.',
    },
    '/sitemap': {
      title: 'Sitemap | AI ToolHub',
      description: 'Browse all sections of AI ToolHub: tools, categories, news, blog, jobs, events, and more.',
    },
    '/wall-of-fame': {
      title: 'Wall of Fame — Top AI Tools & Creators | AI ToolHub',
      description: 'The most loved AI tools and the creators behind them. Celebrate the best of the AI ecosystem.',
    },
    '/leaderboard': {
      title: 'Leaderboard — Top AI Tools This Month | AI ToolHub',
      description: 'See which AI tools are trending right now. Live leaderboard of the most popular tools by category.',
    },
    '/templates': {
      title: 'AI Templates Library | AI ToolHub',
      description: 'Free AI templates, prompts, and starter kits for marketing, design, coding, and content workflows.',
    },
    '/prompts': {
      title: 'AI Prompts Library — Free Prompt Packs | AI ToolHub',
      description: 'Curated AI prompts for ChatGPT, Claude, Midjourney, and more. Browse by category and use case.',
    },
    '/prompt-packs': {
      title: 'AI Prompt Packs — Premium Prompt Collections | AI ToolHub',
      description: 'Curated premium prompt packs for ChatGPT, Claude, Midjourney and more. Boost your AI workflows.',
    },
    '/tool-deals': {
      title: 'AI Tool Deals — Discounts & Lifetime Offers | AI ToolHub',
      description: 'Exclusive AI tool deals, discounts, and lifetime offers. Save on the best AI software available.',
    },
    '/challenges': {
      title: 'AI Challenges — Build Skills with Hands-On Tasks | AI ToolHub',
      description: 'Join AI challenges to learn and earn badges. Hands-on tasks across writing, design, coding, and more.',
    },
    '/ai-learning': {
      title: 'AI Learning Guide — Free Resources | AI ToolHub',
      description: 'Free guides, tutorials, and curated learning paths to master AI tools faster.',
    },
    '/roast-arena': {
      title: 'Roast Arena — Get Brutal AI Feedback on Your Site | AI ToolHub',
      description: 'Submit your landing page or site for an AI-powered roast. Get honest, actionable feedback in seconds.',
    },
    '/quiz/vibe-coding': {
      title: 'Find Your Best Vibe Coding Tool — Quiz | AI ToolHub',
      description: 'Take this 60-second quiz to find the perfect AI vibe coding tool for your project, skill level and budget.',
    },
    '/why-join': {
      title: 'Why Join AI ToolHub — Benefits for Creators & Users',
      description: 'Joining AI ToolHub unlocks tool tracking, badges, sales, marketplace gigs, and AI-powered features.',
    },
    '/join': {
      title: 'Join AI ToolHub — Create Your Free Account',
      description: 'Sign up free to save favorite AI tools, submit your own, earn badges, and access AI search and creator tools.',
    },
    '/credits': {
      title: 'Buy Credits — Power AI Features | AI ToolHub',
      description: 'Buy credits to power AI Search, the LinkedIn Carousel Builder, Landing Page AI and more premium tools.',
    },
    '/premium': {
      title: 'Premium AI Features | AI ToolHub',
      description: 'Unlock premium AI features: smarter search, content builders, advanced analytics and more.',
    },
  };

  const base = staticMap[path];
  if (base) {
    return finalize({
      title: base.title,
      description: truncate(base.description),
      canonical,
      ogType: base.ogType ?? 'website',
      ogImage: base.ogImage ?? DEFAULT_OG_IMAGE,
      robots: base.robots,
    });
  }

  // -- Dynamic routes --
  // /tool/{slug}
  if (path.startsWith('/tool/')) {
    const tool = data.tool;
    if (tool) {
      const cat = tool.category_name || 'AI';
      return finalize({
        title: `${tool.name} Review, Pricing & Alternatives (2026) | AI ToolHub`,
        description: truncate(`${tool.short_description || tool.name + ' review.'} See features, pricing, pros & cons, alternatives, and user reviews of ${tool.name}.`),
        canonical,
        ogType: 'product',
        ogImage: tool.logo_url || tool.screenshot_url || DEFAULT_OG_IMAGE,
        keywords: `${tool.name}, ${tool.name} review, ${tool.name} alternatives, ${cat} AI tools`,
      });
    }
  }

  // /categories/{slug}
  if (path.startsWith('/categories/')) {
    const cat = data.category;
    const count = data.count ?? '';
    if (cat) {
      const countStr = count ? `${count} Options Reviewed` : 'Reviewed';
      return finalize({
        title: `${cat.name} AI Tools — ${countStr} | AI ToolHub`,
        description: truncate(`Compare the best ${cat.name.toLowerCase()} AI tools. Reviews, pricing, alternatives, and trust scores for ${count || 'curated'} ${cat.name.toLowerCase()} options.`),
        canonical,
        ogType: 'website',
        ogImage: DEFAULT_OG_IMAGE,
      });
    }
  }

  // /alternatives/{slug}
  if (path.startsWith('/alternatives/')) {
    const tool = data.tool;
    const count = data.count ?? '';
    if (tool) {
      return finalize({
        title: `${tool.name} Alternatives — ${count || 'Top'} Better Options | AI ToolHub`,
        description: truncate(`Looking for a ${tool.name} alternative? Compare ${count || 'similar'} AI tools with features, pricing and user reviews.`),
        canonical,
        ogType: 'website',
        ogImage: tool.logo_url || DEFAULT_OG_IMAGE,
      });
    }
  }

  // /compare/{slug}
  if (path.startsWith('/compare/') && !staticMap[path]) {
    const slug = path.split('/').pop() || '';
    const label = slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return finalize({
      title: `${label} Comparison | AI ToolHub`,
      description: truncate(`Compare ${label} AI tools head-to-head: features, pricing, pros & cons, and user reviews.`),
      canonical,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
    });
  }

  // /news/{slug}
  if (path.startsWith('/news/')) {
    const article = data.article;
    if (article) {
      return finalize({
        title: `${article.title} | AI News — AI ToolHub`,
        description: truncate(article.excerpt || article.summary || article.title),
        canonical,
        ogType: 'article',
        ogImage: article.image_url || DEFAULT_OG_IMAGE,
        author: article.author_name || article.source_name,
        publishedTime: article.published_at,
        modifiedTime: article.updated_at,
      });
    }
  }

  // /blog/{slug}
  if (path.startsWith('/blog/')) {
    const post = data.post;
    if (post) {
      return finalize({
        title: post.meta_title || `${post.title} | AI ToolHub Blog`,
        description: truncate(post.meta_description || post.excerpt || post.title),
        canonical,
        ogType: 'article',
        ogImage: post.featured_image || DEFAULT_OG_IMAGE,
        author: post.author_name,
        publishedTime: post.published_at,
        modifiedTime: post.updated_at,
      });
    }
  }

  // /jobs/{slug}
  if (path.startsWith('/jobs/')) {
    const job = data.job;
    if (job) {
      return finalize({
        title: `${job.title} at ${job.company_name} | AI Jobs — AI ToolHub`,
        description: truncate(`${job.short_description || job.description || ''} Apply for the ${job.title} role at ${job.company_name}.`),
        canonical,
        ogType: 'website',
        ogImage: job.company_logo || DEFAULT_OG_IMAGE,
      });
    }
  }

  // /events/{slug}
  if (path.startsWith('/events/') && !staticMap[path]) {
    const event = data.event;
    if (event) {
      const dateStr = event.start_date ? new Date(event.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
      const loc = event.location || event.venue_name || (event.event_type === 'virtual' ? 'Virtual' : '');
      return finalize({
        title: `${event.title} ${dateStr ? `— ${dateStr}` : ''} | AI Events — AI ToolHub`.trim(),
        description: truncate(`${event.short_description || event.description}${loc ? ` — ${loc}.` : ''}`),
        canonical,
        ogType: 'article',
        ogImage: event.image_url || DEFAULT_OG_IMAGE,
      });
    }
  }

  // /income-blueprints/{slug}
  if (path.startsWith('/income-blueprints/')) {
    const bp = data.blueprint;
    if (bp) {
      const earnings = bp.estimated_earnings_min && bp.estimated_earnings_max
        ? `$${bp.estimated_earnings_min}–$${bp.estimated_earnings_max}/mo`
        : '';
      return finalize({
        title: `${bp.name} — AI Income Blueprint${earnings ? ` (${earnings})` : ''} | AI ToolHub`,
        description: truncate(`${bp.description} Difficulty: ${bp.difficulty || 'beginner'}. ${earnings ? `Estimated earnings ${earnings}.` : ''}`),
        canonical,
        ogType: 'article',
        ogImage: DEFAULT_OG_IMAGE,
      });
    }
  }

  // /digital-products/{slug}
  if (path.startsWith('/digital-products/')) {
    const product = data.product;
    if (product) {
      return finalize({
        title: `${product.title} — Digital Product | AI ToolHub`,
        description: truncate(product.short_description || product.description),
        canonical,
        ogType: 'product',
        ogImage: product.thumbnail_url || product.preview_url || DEFAULT_OG_IMAGE,
      });
    }
  }

  // Fallback
  return finalize({
    title: 'AI ToolHub — Find AI Tools, Alternatives & Side-by-Side Comparisons',
    description: DEFAULT_DESC,
    canonical,
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
  });
}

function finalize(m: SEOMeta): SEOMeta {
  return {
    ...m,
    robots: m.robots || 'index,follow,max-image-preview:large',
    description: truncate(m.description),
  };
}
